import React, { useState } from "react";
import { Search, Layers, ChevronDown, Info } from "lucide-react";
import { LEXICON_TERMS, FAMILIES } from "../lib/lexiconData";

const UI = {
  fr: {
    searchPlaceholder: "Rechercher un terme (ex : ZII, PBO, liquidité...)",
    noResult: (q) => `Aucun terme trouvé pour « ${q} ».`,
    easy: "En clair",
    tech: "Technique",
    interpretation: "Interprétation",
    usage: "Utilisation",
    recapTitle: "En résumé — à quoi ça sert",
    recapSub: "Une ligne par terme.",
    recapCol1: "Terme",
    recapCol2: "Ce qu'il vérifie / apporte",
    signalTitle: "Comment lire un signal, en résumé",
    signalSteps: [
      "Vérifier la tendance actuelle et le biais dominant sur les unités de temps supérieures.",
      "Vérifier le régime de marché — éviter d'agir en cas de compression ou d'expansion volatile.",
      "Ouvrir le signal : direction, entrée, stop, take-profit, ratio risque/récompense.",
      "Croiser avec la prévision probabiliste (Monte-Carlo) — P(hausse) > 60 % renforce la conviction.",
      "Passer par le calcul de taille de position selon le capital.",
    ],
    acronymNote: null,
  },
  en: {
    searchPlaceholder: "Search a term (e.g. ZII, PBO, liquidity...)",
    noResult: (q) => `No term found for "${q}".`,
    easy: "In plain terms",
    tech: "Technical",
    interpretation: "Interpretation",
    usage: "How it's used",
    recapTitle: "Summary — what each term is for",
    recapSub: "One line per term.",
    recapCol1: "Term",
    recapCol2: "What it checks / brings",
    signalTitle: "How to read a signal, in short",
    signalSteps: [
      "Check the current trend and the dominant bias on higher timeframes.",
      "Check the market regime — stay out during compression or volatile expansion.",
      "Open the signal: direction, entry, stop, take-profit, risk/reward ratio.",
      "Cross-check with the probabilistic forecast (Monte Carlo) — P(up) > 60% strengthens conviction.",
      "Run position sizing against your capital before executing.",
    ],
    acronymNote: "Proprietary acronyms (ZII, ZRI, CSD...) are kept in French in both the app and this glossary — matching exactly what you see on the charts. Each entry gives the English translation.",
  },
};

export default function LexiconContent({ lang = "fr" }) {
  const L = UI[lang] || UI.fr;
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const s = q.trim().toLowerCase();
  const matches = LEXICON_TERMS.filter((t) => {
    if (!s) return true;
    return [t.acronym, t.name[lang], t.easy[lang], t.tech[lang], t.usage[lang]]
      .some((v) => (v || "").toLowerCase().includes(s));
  });

  const byFamily = FAMILIES
    .map((f) => ({ ...f, terms: matches.filter((t) => t.family === f.key) }))
    .filter((f) => f.terms.length > 0);

  return (
    <div>
      {L.acronymNote && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-accent-blue/25 bg-accent-blue/8 px-3.5 py-2.5 text-[12.5px] text-white/70" data-testid="lexicon-acronym-note">
          <Info size={14} className="text-accent-blue shrink-0 mt-0.5" />
          <span>{L.acronymNote}</span>
        </div>
      )}

      <div className="mb-6 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          className="field pl-9"
          placeholder={L.searchPlaceholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="lexique-search"
        />
      </div>

      {byFamily.map((f) => {
        const isCollapsed = collapsed[f.key] && !s;
        return (
          <div key={f.key} className="mb-8" data-testid={`lex-family-${f.key}`}>
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [f.key]: !c[f.key] }))}
              className="w-full flex items-center gap-2 mb-3 group"
              data-testid={`lex-family-toggle-${f.key}`}
            >
              <Layers size={14} className="text-white/40" />
              <div className="uppercase text-[11px] tracking-widest text-white/50 group-hover:text-white/80 transition">
                {f[lang]} <span className="text-white/30">({f.terms.length})</span>
              </div>
              <div className="flex-1 h-px bg-white/5" />
              <ChevronDown size={14} className={`text-white/40 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
            </button>
            {!isCollapsed && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {f.terms.map((t) => (
                  <div key={t.key} className="panel p-4" data-testid={`lex-card-${t.key}`}>
                    <div className="flex items-baseline gap-3 mb-2 flex-wrap">
                      <span className="font-mono text-lg font-bold text-accent-blue">{t.acronym}</span>
                      <span className="font-heading text-[15px]">{t.name[lang]}</span>
                    </div>
                    <Section label={L.easy} color="text-up">{t.easy[lang]}</Section>
                    <Section label={L.tech} color="text-white/70">{t.tech[lang]}</Section>
                    <Section label={L.interpretation} color="text-warn">{t.interpretation[lang]}</Section>
                    <Section label={L.usage} color="text-accent-blue">{t.usage[lang]}</Section>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {matches.length === 0 && (
        <div className="text-center text-white/40 py-16" data-testid="lexique-no-result">{L.noResult(q)}</div>
      )}

      <div className="panel p-5 mt-4" data-testid="lexicon-signal-howto">
        <div className="panel-title text-[14px] mb-2">{L.signalTitle}</div>
        <div className="space-y-2 text-[13px] text-white/70">
          {L.signalSteps.map((step, i) => (
            <p key={i}><span className="text-accent-blue font-medium">{i + 1}.</span> {step}</p>
          ))}
        </div>
      </div>

      <div className="panel p-5 mt-4" data-testid="lexicon-recap-table">
        <div className="panel-title text-[14px] mb-1">{L.recapTitle}</div>
        <p className="text-white/40 text-xs mb-3">{L.recapSub}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-white/40 uppercase text-[10px] tracking-widest border-b border-white/10">
                <th className="py-2 pr-4 font-medium">{L.recapCol1}</th>
                <th className="py-2 font-medium">{L.recapCol2}</th>
              </tr>
            </thead>
            <tbody>
              {LEXICON_TERMS.map((t) => (
                <tr key={t.key} className="border-b border-white/5 last:border-0">
                  <td className="py-2 pr-4 font-mono font-semibold text-accent-blue whitespace-nowrap align-top">{t.acronym}</td>
                  <td className="py-2 text-white/75">{t.oneLiner[lang]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Section({ label, color, children }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className={`text-[10px] uppercase tracking-widest ${color} mb-0.5`}>{label}</div>
      <div className="text-[13px] text-white/85 leading-relaxed">{children}</div>
    </div>
  );
}
